import { Check, CircleX, Flag, LoaderCircle, Split } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AgentSummary, RunListItem, Task } from "../../types";
import { ColorDot } from "../status-badge";
import { Card, CardContent } from "../ui/card";

type StepState = "done" | "current" | "failed" | "pending";

interface RouteStep {
  id: string;
  label: string;
  color: string | undefined;
  state: StepState;
}

/** Each station on the route with how far its latest run got, then the lane placeholder and the finish. */
export function routeSteps(
  task: Task,
  runs: RunListItem[],
  pipeline: AgentSummary[],
  lanePending: boolean,
  finish: string,
): RouteStep[] {
  const latest = new Map<string, RunListItem>();
  for (const run of [...runs].reverse()) latest.set(run.agentId, run);
  const shipped = task.status === "completed";
  return [
    ...pipeline.map((agent): RouteStep => {
      const run = latest.get(agent.id);
      const current = Boolean(
        run &&
          ["queued", "launching", "running", "waiting_for_input", "blocked"].includes(run.status) &&
          !["completed", "cancelled"].includes(task.status),
      );
      const failed = Boolean(run && ["failed", "expired"].includes(run.status));
      const done = run?.status === "completed" && !current;
      const state = failed ? "failed" : current ? "current" : done || shipped ? "done" : "pending";
      return { id: agent.id, label: agent.name, color: agent.station.color, state };
    }),
    ...(lanePending && !shipped
      ? [{ id: "lane", label: "Lane picked by intake", color: undefined, state: "pending" as const }]
      : []),
    { id: "shipped", label: finish, color: undefined, state: shipped ? "done" : "pending" },
  ];
}

export function RouteStepper({
  task,
  runs,
  pipeline,
  lanePending,
  finish,
}: {
  task: Task;
  runs: RunListItem[];
  pipeline: AgentSummary[];
  /** Still in intake: the lane's stations are not known yet. */
  lanePending: boolean;
  finish: string;
}) {
  const steps = routeSteps(task, runs, pipeline, lanePending, finish);
  return (
    <Card className="py-4">
      <CardContent>
        <ol className="flex items-center gap-1 overflow-x-auto pb-1">
          {steps.map((step, i) => (
            <li key={step.id} className="flex shrink-0 items-center gap-1">
              {i > 0 && (
                <span
                  className={cn(
                    "h-px w-4 bg-border @3xl/main:w-8",
                    step.state !== "pending" && "bg-primary/40",
                  )}
                />
              )}
              <span
                aria-current={step.state === "current" ? "step" : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium whitespace-nowrap",
                  step.state === "done" &&
                    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
                  step.state === "current" && "border-primary bg-primary text-primary-foreground shadow-sm",
                  step.state === "failed" && "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
                  step.state === "pending" && "text-muted-foreground",
                  step.id === "lane" && "border-dashed",
                )}
              >
                {step.state === "done" ? (
                  <Check className="size-3.5" />
                ) : step.state === "current" ? (
                  <LoaderCircle className="size-3.5 animate-spin" />
                ) : step.state === "failed" ? (
                  <CircleX className="size-3.5" />
                ) : step.id === "shipped" ? (
                  <Flag className="size-3.5" />
                ) : step.id === "lane" ? (
                  <Split className="size-3.5" />
                ) : (
                  <ColorDot color={step.color ?? "currentColor"} className="size-2 ring-0" />
                )}
                {step.label}
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
